import { ImageBlockSchema } from '@blocksuite/affine-model';
import {
  BlockMarkdownAdapterExtension,
  type BlockMarkdownAdapterMatcher,
  type MarkdownAST,
} from '@blocksuite/affine-shared/adapters';
import { getAssetName } from '@blocksuite/store';
import {
  FetchUtils
} from '@blocksuite/affine-shared/adapters';

import { 
  processImageNodeToBlock, 
  serializeImageMetadata, 
  isValidRemoteUrl 
} from './utils';

const isImageNode = (node: MarkdownAST) => node.type === 'image';

export const imageBlockMarkdownAdapterMatcher: BlockMarkdownAdapterMatcher = {
  flavour: ImageBlockSchema.model.flavour,
  toMatch: o => isImageNode(o.node),
  fromMatch: o => o.node.flavour === ImageBlockSchema.model.flavour,
  toBlockSnapshot: {
    enter: async (o, context) => {
      const { configs, walkerContext, assets, template } = context;
      const imageURL = 'url' in o.node ? o.node.url : '';
      if (!assets || !imageURL) {
        return;
      }
      // 表格内交给 inline 图片处理，避免重复生成 image block
      const parents = walkerContext.stack.map(s => s.node as any);
      const inTable = parents.some(
        p => p?.type === 'table' || p?.type === 'tableRow' || p?.type === 'tableCell'
      );
      if (inTable) return;

      // 🔥 提取 markdown 图片的元数据：alt 和 title
      const alt = 'alt' in o.node ? o.node.alt : null;
      const title = 'title' in o.node ? o.node.title : null;
      
      await processImageNodeToBlock(
        imageURL, 
        walkerContext, 
        assets, 
        configs, 
        template,
        { alt, title, originalUrl: imageURL }
      );
    },
  },
  fromBlockSnapshot: {
    enter: async (o, context) => {
      const { assets, walkerContext, updateAssetIds, template } = context;
      const blobId = (o.node.props.sourceId ?? '') as string;
      const caption = (o.node.props.caption as string | undefined) ?? '';
      const originalUrl = (o.node.props.originalUrl as string | undefined);
      
      let blobName = '', alt = (o.node.props?.alt as string || null);

      // 🔥 标准化 template 参数（兼容 boolean 和 string）
      let exportMode: 'simple' | 'export' | 'llm' = 'llm';
      if (template === true) {
        exportMode = 'simple';
      } else if (template === 'simple') {
        exportMode = 'simple';
      } else if (template === 'export') {
        exportMode = 'export';
      }

      // 🔥 根据 exportMode 决定是否需要处理 assets
      const needAssets = exportMode === 'export' || exportMode === 'llm';
      
      if(FetchUtils.fetchable(blobId) || !needAssets) {
        // simple 模式或远程 URL：直接使用 blobId
        blobName = blobId;
      } else {
        // export/llm 模式：需要生成 assets 路径
        if (!assets) {
          return;
        }
        await assets.readFromBlob(blobId);
        const blob = assets.getAssets().get(blobId);
        if (!blob) {
          return;
        }
        blobName = getAssetName(assets.getAssets(), blobId);
        blobName = `assets/${blobName}`;
        alt = (blob as File).name ?? null
      }

      // 🔥 根据 template 参数决定输出格式
      let finalAlt: string | null = caption || alt;
      let finalUrl = blobName;

      switch (exportMode) {
        case 'simple':
          // 🔥 simple 模式：![caption](originalUrl)
          // 用于 formatMarkdown 等场景，使用原始 URL
          finalAlt = caption || null;
          finalUrl = originalUrl || blobName;
          break;

        case 'export':
          // 🔥 export 模式：![caption](assets/...)
          // 用于用户导出，简洁可读
          finalAlt = caption || null;
          finalUrl = blobName;  // 使用 assets 路径（已包含 assets/ 前缀）
          break;

        case 'llm':
        default:
          // 🔥 llm 模式：![{json}](assets/...)
          // 用于 LLM 处理，保留完整元数据
          if (caption || isValidRemoteUrl(originalUrl)) {
            finalAlt = serializeImageMetadata({
              caption: caption,
              originalUrl: isValidRemoteUrl(originalUrl) ? originalUrl : undefined,
            });
          }
          finalUrl = blobName;  // 使用 assets 路径（已包含 assets/ 前缀）
          break;
      }

      // 🔥 在需要 assets 的模式下，记录 assetId 以便打包
      if (needAssets) {
        updateAssetIds?.(blobId);
      }
      walkerContext
        .openNode(
          {
            type: 'paragraph',
            children: [],
          },
          'children'
        )
        .openNode(
          {
            type: 'image',
            url: finalUrl,
            title: null,
            alt: finalAlt,
          },
          'children'
        )
        .closeNode()
        .closeNode();
    },
  },
};

export const ImageBlockMarkdownAdapterExtension = BlockMarkdownAdapterExtension(
  imageBlockMarkdownAdapterMatcher
);
